import type { ReactNode } from 'react'

// DataTable — ATC-109 / `ATC-ADR-018`.
//
// Ported from Flight Deck's `DataTable.tsx`, minus Fluent and Tailwind. The
// portable core is the column descriptor, the three exclusive states
// (error / loading / empty), and the numeric-column rule.
//
// ATC'S DENSITY AND KEYSET PAGINATION ARE PRESERVED. The conversion is a
// re-skin, not a redesign — so this component deliberately does NOT own
// paging. ATC pages with a continuation token (`EnvironmentsList`), and folding
// a page model in here would push callers toward offset paging, which the
// Licensing list endpoint does not offer.
//
// Selection is also deliberately absent: Flight Deck's version carries a
// checkbox column for bulk device actions, and ATC has no bulk action. Add it
// when a surface needs it, not speculatively.

export interface DataTableColumn<T> {
  key: string
  header: ReactNode
  render: (item: T) => ReactNode
  /**
   * Mono + tabular figures + right-aligned. EVERY NUMERIC GETS THIS — counts,
   * balances, sizes. A column of proportional digits cannot be scanned, and an
   * operator scanning at 11pm is the design target.
   */
  numeric?: boolean
  width?: string
  align?: 'left' | 'right' | 'center'
}

export interface DataTableProps<T> {
  columns: DataTableColumn<T>[]
  data: T[]
  keyForRow: (item: T) => string
  onRowClick?: (item: T) => void
  isLoading?: boolean
  /** Rendered instead of rows. Takes precedence over loading and empty. */
  error?: ReactNode
  /**
   * Rendered when `data` is empty. State the real absent condition in WORDS —
   * "No devices registered", not an em-dash, which reads as a data problem.
   */
  emptyState?: ReactNode
  /**
   * The dark header band. NOT the default, and not decoration: it connects a
   * group header to its expanded micro-table so the rows read as that
   * heading's CONTENTS. A top-level list has nothing above it to connect to.
   */
  headerBand?: boolean
  /**
   * `fixed` makes column widths authoritative rather than hints. Needed when
   * several tables must align with each other — under `auto` each sizes to its
   * own content, so the same column lands at a different x-offset in every one,
   * which reads as a rendering bug even though each table is correct.
   */
  tableLayout?: 'auto' | 'fixed'
  /** Accessible name for the table. */
  caption?: string
}

function cellClass<T>(column: DataTableColumn<T>): string | undefined {
  const classes: string[] = []
  if (column.numeric) classes.push('rx-num')
  // An explicit `align` wins over the numeric default.
  if (column.align === 'right') classes.push('rx-align-right')
  if (column.align === 'center') classes.push('rx-align-center')
  return classes.length > 0 ? classes.join(' ') : undefined
}

export function DataTable<T>({
  columns,
  data,
  keyForRow,
  onRowClick,
  isLoading = false,
  error,
  emptyState,
  headerBand = false,
  tableLayout = 'auto',
  caption,
}: DataTableProps<T>) {
  const tableClass = [
    'rx-table',
    tableLayout === 'fixed' ? 'rx-table-fixed' : null,
    headerBand ? 'rx-table-band' : null,
  ]
    .filter(Boolean)
    .join(' ')

  // Exactly one of error / loading / empty renders, in that order of
  // precedence: an error is more informative than "no rows", and "no rows"
  // while still loading is a lie.
  const state: ReactNode =
    error !== undefined && error !== null
      ? error
      : isLoading
        ? 'Loading…'
        : data.length === 0
          ? (emptyState ?? 'No results')
          : null

  return (
    <table className={tableClass}>
      {caption && <caption className="rx-visually-hidden">{caption}</caption>}
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column.key} scope="col" style={column.width ? { width: column.width } : undefined}>
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {state !== null ? (
          <tr className="rx-table-state">
            <td colSpan={columns.length}>{state}</td>
          </tr>
        ) : (
          data.map((item) => (
            <tr
              key={keyForRow(item)}
              className={onRowClick ? 'rx-table-row-clickable' : undefined}
              onClick={onRowClick ? () => onRowClick(item) : undefined}
            >
              {columns.map((column) => (
                <td key={column.key} className={cellClass(column)}>
                  {column.render(item)}
                </td>
              ))}
            </tr>
          ))
        )}
      </tbody>
    </table>
  )
}
